export function hasPermission(moduleName, action = 'read') {
  const rawRole = localStorage.getItem('userRole') || localStorage.getItem('role') || '';
  const role = rawRole.toLowerCase().trim().replace(/\s+/g, '');

  // Superadmins ALWAYS have full unrestricted access
  if (['superadmin', 'super admin', '1'].includes(role) || role === 'superadmin') {
    return true;
  }

  const targetKey = String(moduleName || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const isDoctorRole = role.includes('doctor') || role.includes('physician');

  // Doctors & Physicians ALWAYS have access to Medical Dashboard
  if (targetKey === 'medicaldashboard' && isDoctorRole) {
    return true;
  }

  // For doctor role, strictly verify against their explicit role permissions in user session
  if (isDoctorRole) {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      if (u.role && typeof u.role === 'object') {
        let explicitRolePerms = u.role.permissions || u.role.modules || [];
        if (typeof explicitRolePerms === 'string') {
          try { explicitRolePerms = JSON.parse(explicitRolePerms); } catch (e) { explicitRolePerms = [explicitRolePerms]; }
        }
        if (Array.isArray(explicitRolePerms)) {
          const hasInRole = explicitRolePerms.some(p => {
            const pStr = String(typeof p === 'object' && p !== null ? (p.code || p.name || p.module_name || p.slug || p.id) : p)
              .toLowerCase().replace(/[^a-z0-9]/g, '');
            if (!pStr || !targetKey) return false;
            return pStr === targetKey || targetKey.includes(pStr) || pStr.includes(targetKey);
          });
          if (!hasInRole) {
            return false;
          }
        }
      }
    } catch (e) {
      console.warn("Doctor role permission check note:", e);
    }
  }

  try {
    const rawUserModulesStr = localStorage.getItem('allowedModules') ||
      localStorage.getItem('userModules') ||
      localStorage.getItem('permissions');

    if (rawUserModulesStr !== null && rawUserModulesStr !== undefined) {
      let userModules = [];
      try {
        userModules = JSON.parse(rawUserModulesStr);
      } catch (e) {
        userModules = [];
      }

      if (Array.isArray(userModules)) {
        if (userModules.length === 0) return false;
        if (userModules.includes('*') || userModules.includes('all')) return true;

        const targetKey = String(moduleName || '').toLowerCase().replace(/[^a-z0-9]/g, '');
        const reqAction = action.toLowerCase().trim();

        const actionSynonyms = {
          'view': 'read', 'list': 'read', 'read': 'read',
          'add': 'create', 'create': 'create',
          'edit': 'update', 'update': 'update',
          'delete': 'delete', 'remove': 'delete'
        };
        const normAction = actionSynonyms[reqAction] || reqAction;

        // Dynamic candidate string matcher
        const isDynamicMatch = (candidate) => {
          if (!candidate) return false;

          let candidateStrings = [];
          if (typeof candidate === 'object' && candidate !== null) {
            if (candidate.code) candidateStrings.push(String(candidate.code));
            if (candidate.module_name) candidateStrings.push(String(candidate.module_name));
            if (candidate.name) candidateStrings.push(String(candidate.name));
            if (candidate.slug) candidateStrings.push(String(candidate.slug));
            if (candidate.module) candidateStrings.push(String(candidate.module));
            if (candidate.title) candidateStrings.push(String(candidate.title));
            if (Array.isArray(candidate.aliases)) {
              candidate.aliases.forEach(a => candidateStrings.push(String(a)));
            }
          } else {
            candidateStrings.push(String(candidate));
          }

          return candidateStrings.some(str => {
            const clean = str.toLowerCase().replace(/[^a-z0-9]/g, '');
            if (!clean || !targetKey) return false;

            // 1. Exact match
            if (clean === targetKey) return true;

            // 2. Branch & Department stem matching (handles 'Branch Management' <-> 'branches' <-> 'department')
            if (
              (clean.startsWith('branch') || clean.startsWith('department')) &&
              (targetKey.startsWith('branch') || targetKey.startsWith('department'))
            ) {
              return true;
            }

            // 3. User & Role matching (distinguishes 'usermanagement' from 'usergroup')
            if (
              ['usermanagement', 'user', 'users', 'edituser', 'userregister'].includes(clean) &&
              ['usermanagement', 'user', 'users'].includes(targetKey)
            ) {
              return true;
            }

            if (clean === 'roles' && targetKey === 'role') return true;
            if (clean === 'role' && targetKey === 'roles') return true;

            // 4. Singular / Plural equality
            if (clean.endsWith('s') && clean.slice(0, -1) === targetKey) return true;
            if (targetKey.endsWith('s') && targetKey.slice(0, -1) === clean) return true;
            if (clean.endsWith('es') && clean.slice(0, -2) === targetKey) return true;
            if (targetKey.endsWith('es') && targetKey.slice(0, -2) === clean) return true;

            return false;
          });
        };

        return userModules.some(perm => {
          // Format 1: Scoped string e.g. "tokens:read" or "Tokens"
          if (typeof perm === 'string' && isNaN(perm)) {
            if (perm === '*') return true;
            const parts = perm.split(':');
            if (parts.length === 2) {
              const modPart = parts[0];
              const actPart = actionSynonyms[parts[1].toLowerCase().trim()] || parts[1].toLowerCase().trim();
              if (isDynamicMatch(modPart)) {
                return actPart === normAction || actPart === '*';
              }
            } else {
              if (isDynamicMatch(perm)) return true;
            }
          }

          // Format 2: Object format e.g. { code: "branches", actions: ["read"] }
          if (typeof perm === 'object' && perm !== null) {
            if (isDynamicMatch(perm)) {
              if (Array.isArray(perm.actions) && perm.actions.length > 0) {
                const normActions = perm.actions.map(a => actionSynonyms[String(a).toLowerCase().trim()] || String(a).toLowerCase().trim());
                return normActions.includes(normAction) || normActions.includes('*');
              }
              return true;
            }
          }

          // Format 3: Numeric ID lookup (supports ID 6 and legacy ID 66 for Branch/Department)
          if (typeof perm === 'number' || (typeof perm === 'string' && !isNaN(perm) && perm.trim() !== '')) {
            const numId = Number(perm);
            if ((numId === 6 || numId === 66) && (targetKey.startsWith('branch') || targetKey.startsWith('department'))) {
              return true;
            }

            const sysModulesStr = localStorage.getItem('systemModules');
            if (sysModulesStr) {
              try {
                const sysModules = JSON.parse(sysModulesStr);
                const sysObj = Array.isArray(sysModules) && sysModules.find(m => Number(m.id || m.module_id) === numId);
                if (sysObj && isDynamicMatch(sysObj)) return true;
              } catch (e) {
                console.warn("Could not parse systemModules from localStorage:", e);
              }
            }
          }

          return false;
        });
      }
    }

    if (role === 'superadmin' || role === '1') {
      return true;
    }

    return false;
  } catch (e) {
    console.error("Error in hasPermission:", e);
    return false;
  }
}

export function hasModulePermission(userPermissions, moduleName) {
  if (!userPermissions || !Array.isArray(userPermissions)) return false;
  const targetKey = String(moduleName || '').toLowerCase().replace(/[^a-z0-9]/g, '');

  return userPermissions.some(p => {
    if (p === '*' || p === 'all') return true;
    const pStr = String(typeof p === 'object' && p !== null ? (p.code || p.name || p.module_name || p.id) : p).toLowerCase().replace(/[^a-z0-9]/g, '');

    if (pStr === targetKey) return true;
    if (
      (pStr.startsWith('branch') || pStr.startsWith('department') || pStr === '6' || pStr === '66') &&
      (targetKey.startsWith('branch') || targetKey.startsWith('department'))
    ) {
      return true;
    }

    if (pStr.endsWith('s') && pStr.slice(0, -1) === targetKey) return true;
    if (targetKey.endsWith('s') && targetKey.slice(0, -1) === pStr) return true;

    return false;
  });
}




